<template>
  <div class="timeline-calendar">
    <div class="d-flex">
      <div v-for="day in weekdays" :key="day.key" class="day-timeline-col">
        <!-- Day Header -->
        <div class="day-header text-center mb-2">
          <div class="fw-semibold">{{ day.label }}</div>
        </div>

        <!-- Hour markers -->
        <div class="hour-markers-horizontal">
          <span
            v-for="(hour, index) in hourMarkers"
            :key="`${day.key}-${hour}`"
            class="hour-mark"
            :class="{ 'major-hour': hour === 0 }"
            :style="{ left: `${(hour / 24) * 100}%` }"
          >
            {{ hour }}
          </span>
        </div>

        <!-- Timeline bars area -->
        <div class="timeline-bars-area position-relative">
          <!-- Hour grid lines -->
          <div class="hour-grid">
            <div
              v-for="(line, index) in gridLinePositions"
              :key="`grid-${day.key}-${index}`"
              class="hour-line"
              :class="{ major: line.isMajor, divider: line.isDivider }"
              :style="{ left: `${line.position}%` }"
            ></div>
          </div>

          <!-- Current time indicator -->
          <div
            v-if="currentDayKey === day.key"
            class="current-time-line"
            :style="{ left: `${currentTimePosition}%` }"
            v-b-tooltip.hover
            :title="getCurrentTimeTooltipText()"
          ></div>

          <!-- Shift bars -->
          <div
            v-for="shift in getShiftsForDay(day.key)"
            :key="shift.id + '-' + shift._barType"
            v-b-tooltip.hover.html
            class="shift-bar-wrapper"
            :class="shift._barType"
            :style="getShiftBarStyle(shift)"
            :title="getShiftTooltipText(shift)"
            @click="$emit('shift-click', shift)"
          >
            <div class="shift-bar-main" :style="{ backgroundColor: '#' + shift.color }"></div>
            <div class="shift-bar-shadow" :style="{ backgroundColor: '#' + shift.color }"></div>
          </div>

          <!-- Preview bars (for form mode) -->
          <div
            v-for="(bar, index) in getPreviewBarsForDay(day.key)"
            :key="'preview-' + day.key + '-' + index"
            class="shift-bar-wrapper shift-preview-wrapper"
            :class="[bar.type, { 'has-conflict': hasConflict }]"
            :style="getPreviewBarStyleByType(day.key, bar)"
          >
            <i
              v-if="hasConflict && bar.type !== 'overnight-end'"
              class="mc-fa icon-fill_alert-triangle shift-alert-icon"
            ></i>
            <div class="shift-bar-main" :style="{ backgroundColor: '#' + previewColor }"></div>
            <div
              class="shift-bar-shadow"
              :style="{ backgroundColor: '#' + previewColor, opacity: 0.3 }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShiftTimelineCalendar',

  props: {
    shifts: {
      type: Array,
      default: () => [],
    },
    showPreview: {
      type: Boolean,
      default: false,
    },
    previewDays: {
      type: Array,
      default: () => [],
    },
    previewStart: {
      type: String,
      default: '',
    },
    previewEnd: {
      type: String,
      default: '',
    },
    hourMarkers: {
      type: Array,
      default: () => [0, 6, 12, 18],
    },
    previewColor: {
      type: String,
      default: '#FDD835',
    },
    hasConflict: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentTime: new Date(),
      timeInterval: null,
    }
  },

  computed: {
    weekdays() {
      return [
        { key: 1, label: this.$t('global.days.monday') },
        { key: 2, label: this.$t('global.days.tuesday') },
        { key: 3, label: this.$t('global.days.wednesday') },
        { key: 4, label: this.$t('global.days.thursday') },
        { key: 5, label: this.$t('global.days.friday') },
        { key: 6, label: this.$t('global.days.saturday') },
        { key: 7, label: this.$t('global.days.sunday') },
        { key: 8, label: this.$t('global.days.monday') }, // overflow for next Monday
      ]
    },
    currentDayKey() {
      const jsDay = this.currentTime.getDay()
      const isoDay = jsDay === 0 ? 7 : jsDay
      return isoDay
    },

    currentTimePosition() {
      const hours = this.currentTime.getHours()
      const minutes = this.currentTime.getMinutes()
      return ((hours * 60 + minutes) / 1440) * 100
    },

    gridLinePositions() {
      const lines = []
      for (let hour = 0; hour <= 24; hour += 3) {
        lines.push({
          position: (hour / 24) * 100,
          isMajor: hour % 6 === 0, // full height at 0, 6, 12, 18, 24
          isDivider: hour === 0 || hour === 24, // dark color only at 0 and 24
        })
      }
      return lines
    },
  },

  mounted() {
    this.timeInterval = setInterval(() => {
      this.currentTime = new Date()
    }, 60000) // update every minute
  },

  beforeDestroy() {
    clearInterval(this.timeInterval)
  },

  methods: {
    getShiftsForDay(dayKey) {
      const actualDay = dayKey === 8 ? 1 : dayKey
      const prevDay = actualDay === 1 ? 7 : actualDay - 1

      const result = []

      this.shifts.forEach((shift) => {
        const isOvernight = shift.isOvernight || shift.endTime < shift.startTime

        // Normal part: shift starts on this day
        if (shift.days?.includes(actualDay)) {
          result.push({
            ...shift,
            _barType: isOvernight ? 'overnight-start' : 'normal',
          })
        }

        // Overnight continuation: shift started yesterday, ends today
        if (isOvernight && shift.days?.includes(prevDay)) {
          result.push({
            ...shift,
            _barType: 'overnight-end',
          })
        }
      })

      return result
    },

    getShiftBarStyle(shift) {
      const [startHour, startMin] = shift.startTime.split(':').map(Number)
      const [endHour, endMin] = shift.endTime.split(':').map(Number)

      const startMinutes = startHour * 60 + startMin
      const endMinutes = endHour * 60 + endMin

      let leftPercent, widthPercent

      if (shift._barType === 'overnight-start') {
        // Show from startTime to midnight (24:00)
        leftPercent = (startMinutes / 1440) * 100
        widthPercent = ((1440 - startMinutes) / 1440) * 100
      } else if (shift._barType === 'overnight-end') {
        // Show from midnight (00:00) to endTime
        leftPercent = 0
        widthPercent = (endMinutes / 1440) * 100
      } else {
        // Normal shift
        leftPercent = (startMinutes / 1440) * 100
        widthPercent = ((endMinutes - startMinutes) / 1440) * 100
      }

      return {
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
      }
    },
    getShiftTooltipText(shift) {
      const name = shift?.name || '-'
      const start = shift?.startTime || ''
      const end = shift?.endTime || ''
      const [sh, sm] = start.split(':').map(Number)
      const [eh, em] = end.split(':').map(Number)
      const startMinutes = (sh || 0) * 60 + (sm || 0)
      const endMinutes = (eh || 0) * 60 + (em || 0)
      const overnight = shift?.isOvernight || endMinutes < startMinutes
      return `
    <div class="shift-tooltip">
      ${name}<br />
      ${start} - ${end} WIB${overnight ? ' (+1 hari)' : ''}
    </div>`
    },
    getCurrentTimeTooltipText() {
      const dayLabel = this.weekdays.find((day) => day.key === this.currentDayKey)?.label || ''
      const hours = this.currentTime.getHours().toString().padStart(2, '0')
      const minutes = this.currentTime.getMinutes().toString().padStart(2, '0')
      return `${dayLabel}, ${hours}:${minutes} WIB`
    },
    getPreviewBarsForDay(dayKey) {
      if (!this.showPreview || !this.previewStart || !this.previewEnd) {
        return []
      }

      const actualDay = dayKey === 8 ? 1 : dayKey
      const prevDay = actualDay === 1 ? 7 : actualDay - 1
      const isOvernight = this.previewEnd < this.previewStart

      const bars = []

      if (this.previewDays.includes(actualDay)) {
        bars.push({
          type: isOvernight ? 'overnight-start' : 'normal',
        })
      }

      if (isOvernight && this.previewDays.includes(prevDay)) {
        bars.push({
          type: 'overnight-end',
        })
      }
      return bars
    },
    getPreviewBarStyleByType(dayKey, bar) {
      const [startHour, startMin] = this.previewStart.split(':').map(Number)
      const [endHour, endMin] = this.previewEnd.split(':').map(Number)

      const startMinutes = startHour * 60 + startMin
      const endMinutes = endHour * 60 + endMin

      let leftPercent, widthPercent

      if (bar.type === 'overnight-end') {
        // 00:00 to endTime
        leftPercent = 0
        widthPercent = (endMinutes / 1440) * 100
      } else if (bar.type === 'overnight-start') {
        // startTime to 24:00
        leftPercent = (startMinutes / 1440) * 100
        widthPercent = ((1440 - startMinutes) / 1440) * 100
      } else {
        // Normal
        leftPercent = (startMinutes / 1440) * 100
        widthPercent = ((endMinutes - startMinutes) / 1440) * 100
      }

      return {
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
